<script setup lang="ts">
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { consola } from 'consola';

// Состояние анимации
import { computed, ref } from 'vue';

// Пропсы
const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  color: {
    type: String,
    required: true,
    default: 'sky',
  },
  size: {
    type: String,
    default: 'xl',
  },
  variant: {
    type: String,
    default: 'solid',
  },
  rounded: {
    type: String,
    default: 'rounded-full',
  },
  icon: {
    type: String,
    default: null,
  },
  block: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Красить ли кнопку фирменным акцентом заведения (работает для color="red").
   * false — оставить системный красный: для «удалить», «очистить» и подобных,
   * где красный означает опасность, а не бренд.
   */
  accent: {
    type: Boolean,
    default: true,
  },
});

// Для проброса событий наружу (emit)
const emit = defineEmits(['click']);

const logger = consola.withTag('ButtonMain');

// Стили кнопки
const solid = {
  disabled: `disabled:bg-${props.color}-300 disabled:text-${props.color}-400 disabled:opacity-100 dark:disabled:bg-${props.color}-300 `,
  dark: `dark:text-red dark:bg-${props.color}-800 active:scale-[0.9]`,
  darkFocus: `dark:focus-visible:outline-${props.color}-400`,
  darkHover: `dark:hover:bg-${props.color}-600`,
  light: `shadow-sm text-white bg-${props.color}-500   active:scale-[0.9] active:bg-${props.color}-950 active:text-white focus:ring-${props.color}-900 focus-visible:outline-${props.color}-900`,
  lightFocus: `focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-${props.color}-900`,
  lightHover: `hover:bg-${props.color}-700 hover:text-white`,
};
const outline = {
  disabled: `disabled:bg-${props.color}-300 disabled:text-${props.color}-400 disabled:opacity-100 dark:disabled:bg-${props.color}-300 dark:disabled:border dark:disabled:ring-${props.color}-300 disabled:border disabled:ring-${props.color}-300 dark:disabled:text-${props.color}-400`,
  dark: `dark:text-${props.color}-600 dark:border-${props.color}-600 dark:bg-transparent dark:ring-${props.color}-600 active:scale-[0.9]`,
  darkFocus: `dark:focus-visible:border-${props.color}-900`,
  darkHover: `dark:hover:bg-${props.color}-900 dark:hover:text-white dark:hover:border-${props.color}-900 dark:hover:ring-${props.color}-900`,
  light: `ring-${props.color}-950 border-current bg-transparent text-${props.color}-950 active:scale-[0.9] focus:ring-${props.color}-900`,
  lightFocus: `focus-visible:ring-2 focus-visible:border-${props.color}-500`,
  lightHover: `hover:ring-${props.color}-950 hover:bg-${props.color}-950 hover:text-${props.color}-950`,
};
const soft = {
  disabled: `dark:disabled:bg-transparent disabled:ring-${props.color}-300 dark:disabled:text-${props.color}-400 disabled:bg-${props.color}-300 disabled:text-${props.color}-400 disabled:opacity-100 dark:disabled:bg-${props.color}-300 `,
  dark: `dark:text-white dark:bg-${props.color}-700 active:scale-[0.9]`,
  darkFocus: `dark:focus-visible:ring-${props.color}-700`,
  darkHover: `dark:hover:bg-${props.color}-950 dark:hover:text-white`,
  light: `text-${props.color}-500 bg-${props.color}-300 active:scale-[0.9] focus:ring-${props.color}-900`,
  lightFocus: `focus-visible:ring-2 focus-visible:ring-${props.color}-500`,
  lightHover: `hover:bg-${props.color}-100 hover:text-${props.color}-600`,
};
const disabledClass = `btn-disabled-${props.color}`;
const btnStyle = {
  base: 'focus:outline-none focus-visible:outline-0 disabled:cursor-not-allowed justify-center items-center',
  size: {
    '2sm': 'text-sm',
    sm: 'text-sm',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-base',
    xxl: 'text-2xl',
  },
  padding: {
    '2sm': 'py-0.5 px-0.5',
    sm: 'py-0.5 px-0.5',
    md: 'p-2',
    lg: 'py-2 px-4',
    xl: 'py-3 px-6',
    xxl: 'py-3.5 px-6',
  },
  rounded: props.rounded,
  font: 'font-semibold',
  variant: {
    outline: `${outline.light} ${outline.lightFocus} ${outline.lightHover} ${outline.dark} ${outline.darkFocus} ${outline.darkHover} ${outline.disabled}`,
    solid: `${solid.light} ${solid.lightFocus} ${solid.lightHover} ${solid.dark} ${solid.darkFocus} ${solid.darkHover} ${solid.disabled}`,
    soft: `${soft.light} ${soft.lightFocus} ${soft.lightHover} ${soft.dark} ${soft.darkFocus} ${soft.darkHover} ${soft.disabled}`,
  },
  icon: {
    base: 'flex-shrink-0',
    size: {
      '2xs': 'h-4 w-4',
      xs: 'h-4 w-4',
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-5 w-5',
      xl: 'h-6 w-6',
    },
  },
};

/**
 * Фирменный акцент вместо жёсткого красного.
 *
 * `color="red"` — де-факто основная кнопка всего приложения: каталог, карточка товара,
 * корзина, «Оформить заказ», авторизация, личный кабинет. Красить их по одной означало бы
 * править сорок мест и забыть про сорок первое, поэтому цвет подменяется здесь, в одном
 * компоненте: класс `.btn-accent` читает `--app-accent` из настроек заведения
 * (см. shared/config/appearance.ts и assets/css/appearance.scss).
 *
 * `accent: false` — явный отказ для кнопок, которым красный нужен как «опасно»,
 * а не как «бренд» (удалить профиль, очистить корзину).
 */
const accentClasses = computed(() => {
  if (props.accent === false || props.color !== 'red') return [];
  // Скругление подменяем только у кнопок с дефолтной формой: если вызывающий код
  // задал свой `rounded`, значит форма важна для вёрстки — не трогаем.
  return props.rounded === 'rounded-full' ? ['btn-accent', 'btn-accent--radius'] : ['btn-accent'];
});

const isPressed = ref(false);
let hasUserInteracted = false;

// Отслеживаем первое взаимодействие пользователя со страницей
if (process.client) {
  const markInteraction = () => {
    hasUserInteracted = true;
    // Удаляем слушатели после первого взаимодействия
    document.removeEventListener('touchstart', markInteraction);
    document.removeEventListener('mousedown', markInteraction);
    document.removeEventListener('click', markInteraction);
  };

  document.addEventListener('touchstart', markInteraction, { once: true, passive: true });
  document.addEventListener('mousedown', markInteraction, { once: true, passive: true });
  document.addEventListener('click', markInteraction, { once: true, passive: true });
}

// Функция для виброотклика и анимации
function handlePointerDown() {
  isPressed.value = true;
}
function handlePointerUp() {
  isPressed.value = false;
}
function handlePointerLeave() {
  isPressed.value = false;
}
async function handleClick(e: MouseEvent) {
  // Вибрация вызывается только на реальном клике пользователя
  // и только на нативных платформах (iOS/Android), не в браузере
  if (hasUserInteracted && e.isTrusted && Capacitor.isNativePlatform() && Capacitor.getPlatform() !== 'web') {
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch (error) {
      // Ошибки игнорируем на web/эмуляторе
      logger.debug('Haptics not available:', error);
    }
  }
  emit('click', e);
}
</script>

<template>
  <UButton
    role="button"
    :class="[disabledClass, accentClasses, { 'btn-pressed': isPressed }]"
    :disabled="disabled"
    :loading="loading"
    :aria-label="ariaLabel"
    :ui="btnStyle"
    :variant="variant"
    :icon="icon"
    :label="label"
    :block="block"
    :size="size"
    @click="handleClick"
    @pointerdown="handlePointerDown"
    @pointerup="handlePointerUp"
    @pointerleave="handlePointerLeave"
    @pointercancel="handlePointerLeave"
    @touchend="handlePointerUp"
    @touchcancel="handlePointerLeave"
  >
    <!-- Слот нужен, когда внутри кнопки требуется разметка (цена со старой ценой).
         Передаём его в UButton ТОЛЬКО если родитель действительно что-то передал:
         иначе UButton посчитает слот заданным и перестанет показывать label. -->
    <template v-if="$slots.default" #default>
      <slot />
    </template>

    <!-- Иконка разметкой, а не строкой в пропе `icon`.
         Нужно для анимированных иконок: проп `icon` рендерится CSS-маской, а SVG
         внутри маски браузер держит в статичном режиме и не анимирует вовсе. -->
    <template v-if="$slots.leading" #leading>
      <slot name="leading" />
    </template>
  </UButton>
</template>

<style lang="scss">
.btn-disabled-sky {
  @apply disabled:bg-sky-300 disabled:text-sky-400;
}
.btn-disabled-red {
  @apply disabled:bg-red-300 disabled:text-red-400;
}

.btn-pressed {
  transform: scale(0.96);
  transition: transform 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.UButton {
  transition: transform 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
